import type { Move } from '../../../schemas/move';

/**
 * Archetipi del Chierico (manuale V1, rr. 2670-2676).
 *
 * 5 mosse di tipo `class_special`. Pattern razza-tematico standard, con
 * un'eccezione strutturale notevole:
 *
 * - **Umano**: aggiunge alla **Tradizione Sacra del Chierico** un
 *   incantesimo di 1° livello dalla **lista del Mago**. La `choices`
 *   strutturata sono gli 8 spell L1 del Mago (`mago.allarme`,
 *   `mago.ammaliare-persona`, ecc., con `choiceCount: 1`).
 * - **Nano**: aggiunge l'orazione *Voce della Terra* (di livello 0). Il
 *   collegamento è espresso via `SpellAccess.requiresArchetype: 'nano'`
 *   sull'oggetto Spell, non come scelta su questa mossa.
 *
 * Pattern simmetrico all'archetipo Umano del Mago (`mago.archetipo-umano`),
 * popolato in questa stessa PR con `choices` sui spell L1 del Chierico.
 */

const archetipoUmano: Move = {
  id: 'chierico.archetipo-umano',
  name: 'Archetipo: Umano',
  type: 'class_special',
  classId: 'chierico',
  trigger: "Alla creazione del personaggio (alla scelta dell'archetipo)",
  description:
    'La tua fede spazia oltre i rigidi dettami della teologia classica: aggiungi alla tua Tradizione Sacra un incantesimo di primo livello dalla lista del Mago (vedi `choices`).',
  tags: [],
  prerequisites: [],
  replaces: [],
  // 8 opzioni: gli 8 spell di Livello 1 del Mago.
  choices: [
    { id: 'mago.allarme', label: 'Allarme' },
    { id: 'mago.ammaliare-persona', label: 'Ammaliare Persona' },
    { id: 'mago.comprensione-dei-linguaggi', label: 'Comprensione dei Linguaggi' },
    { id: 'mago.contattare-gli-spiriti', label: 'Contattare gli Spiriti' },
    { id: 'mago.dardo-incantato', label: 'Dardo Incantato' },
    { id: 'mago.individuazione-del-magico', label: 'Individuazione del Magico' },
    { id: 'mago.invisibilita', label: 'Invisibilità' },
    { id: 'mago.telepatia', label: 'Telepatia' },
  ],
  choiceCount: 1,
};

const archetipoElfo: Move = {
  id: 'chierico.archetipo-elfo',
  name: 'Archetipo: Elfo',
  type: 'class_special',
  classId: 'chierico',
  trigger: 'Quando *discerni la realtà*',
  description:
    'Aggiungi «*Cosa prevede il mio destino riguardo la situazione corrente?*» alle domande che puoi fare.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoNano: Move = {
  id: 'chierico.archetipo-nano',
  name: 'Archetipo: Nano',
  type: 'class_special',
  classId: 'chierico',
  trigger: "Alla creazione del personaggio (alla scelta dell'archetipo)",
  description:
    "Aggiungi alla tua Tradizione Sacra l'orazione *Voce della Terra* (livello 0). L'accesso è gestito automaticamente: l'orazione è disponibile per il PG con archetipo Nano in qualunque Tradizione del Chierico.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoHalfling: Move = {
  id: 'chierico.archetipo-halfling',
  name: 'Archetipo: Halfling',
  type: 'class_special',
  classId: 'chierico',
  trigger: 'Quando sei in un insediamento civilizzato pieno di devoti, o in un luogo sacro',
  description:
    "Ti si presenterà sempre l'opportunità di un incarico o di una missione per la tua chiesa o per il tuo dio.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoMezzorco: Move = {
  id: 'chierico.archetipo-mezzorco',
  name: "Archetipo: Mezz'orco",
  type: 'class_special',
  classId: 'chierico',
  trigger: 'Quando *parlamenti*',
  description:
    'La tua divinità è terribile e vendicativa. Puoi sempre usare la minaccia della sua ira come leva.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const chiericoArchetypeMoves: readonly Move[] = [
  archetipoUmano,
  archetipoElfo,
  archetipoNano,
  archetipoHalfling,
  archetipoMezzorco,
] as const;
